<template>
  <v-dialog
    :value="!!invite"
    max-width="560"
    persistent
  >
    <v-card
      v-if="invite"
      class="pa-6"
    >
      <div class="d-flex align-center">
        <v-avatar
          v-if="invite.brandImageUrl"
          class="mr-3"
          size="48"
        >
          <v-img :src="invite.brandImageUrl" />
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-bold">
            {{ $t('BrandInviteTitle') }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t('BrandInviteExpires', { date: formatDate(invite.expiresAt) }) }}
          </div>
        </div>
      </div>

      <div class="text-body-1 mt-4">
        {{ $t('BrandInviteMessage', { brand: invite.brandName }) }}
      </div>

      <div class="text-subtitle-2 mt-4">
        {{ $t('BrandInviteAfterJoin') }}
      </div>
      <ul class="text-body-2 mt-1">
        <li>{{ $t('BrandInviteEffectCards') }}</li>
        <li v-if="systemNames">
          {{ $t('BrandInviteEffectSystems', { names: systemNames }) }}
        </li>
        <li v-if="cardNames">
          {{ $t('BrandInviteEffectStampCards', { names: cardNames }) }}
        </li>
        <li v-if="invite.impact && invite.impact.memberCount > 0">
          {{ $t('BrandInviteEffectMembers', { count: invite.impact.memberCount }) }}
        </li>
        <li>{{ $t('BrandInviteEffectVouchers') }}</li>
      </ul>

      <v-alert
        v-if="confirming"
        class="mt-4 mb-0"
        dense
        text
        :type="confirming === 'accept' ? 'warning' : 'info'"
      >
        {{ confirming === 'accept'
          ? $t('BrandInviteConfirmAccept', { brand: invite.brandName })
          : $t('BrandInviteConfirmReject', { brand: invite.brandName }) }}
      </v-alert>

      <div class="d-flex flex-wrap mt-6">
        <v-btn
          :disabled="working"
          text
          @click="later"
        >
          {{ $t('BrandInviteLater') }}
        </v-btn>
        <v-spacer />
        <template v-if="!confirming">
          <v-btn
            class="mr-2"
            outlined
            @click="confirming = 'reject'"
          >
            {{ $t('BrandInviteReject') }}
          </v-btn>
          <v-btn
            color="primary"
            elevation="0"
            @click="confirming = 'accept'"
          >
            {{ $t('BrandInviteAccept') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            :disabled="working"
            class="mr-2"
            outlined
            @click="confirming = null"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            :color="confirming === 'accept' ? 'primary' : 'error'"
            :loading="working"
            elevation="0"
            @click="decide"
          >
            {{ confirming === 'accept' ? $t('BrandInviteAccept') : $t('BrandInviteReject') }}
          </v-btn>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs'
import { hillo } from 'hillo'
import GlobalConfig, { getOfflineUrl, requireOfflineMode } from '@/model/global/GlobalSettings'
import { acceptShopInvite, getReceivedShopInvites, rejectShopInvite } from '@/model/dataLayer/api/Vip/ShopInviteApi'
import { showErrorMessage, showSuccessMessage } from '@/model/utils/utils'

/**
 * 启动时检查本店有没有收到品牌的加入邀请（云端会员后台发出）。有就弹窗，让店里接受或拒绝；
 * 按设备号认门店，不需要账号。线上和离线（?Base=）模式都能用，只要设备号已知、能连到云端。
 * 「稍后决定」只关掉这一次，下次打开后台还会提示。
 */
export default {
  name: 'BrandInviteDialog',
  data () {
    return {
      invites: [],
      deviceId: null,
      confirming: null,
      working: false
    }
  },
  computed: {
    invite () {
      return this.invites[0] || null
    },
    systemNames () {
      return (this.invite?.impact?.ownSystems || []).filter(s => s.active).map(s => s.name).join('、')
    },
    cardNames () {
      return (this.invite?.impact?.ownStampCards || []).map(c => c.name).join('、')
    }
  },
  async mounted () {
    this.deviceId = await this.resolveDeviceId()
    if (!this.deviceId) return
    try {
      this.invites = await getReceivedShopInvites(this.deviceId)
    } catch (e) {
      // 云端还没上这个接口或网络不通：不打扰店里
      console.log('brand invites unavailable', e)
    }
  },
  methods: {
    /**
     * 离线模式下 GlobalConfig.DeviceId 在连不上店内主机时会被兜底成 9101，
     * 拿它去查邀请会看到别的店的。这里只认店内主机亲口报的设备号，拿不到就不提示。
     */
    async resolveDeviceId () {
      if (!requireOfflineMode()) return GlobalConfig.DeviceId || null
      const res = await hillo.silentGet(getOfflineUrl() + '/PHP/AccessLog.php?op=deviceId')
      return res?.content || null
    },
    formatDate (value) {
      return value ? dayjs(value).format('YYYY-MM-DD') : ''
    },
    later () {
      this.confirming = null
      this.invites = []
    },
    async decide () {
      const invite = this.invite
      const deviceId = this.deviceId
      this.working = true
      try {
        if (this.confirming === 'accept') {
          await acceptShopInvite(invite.id, deviceId)
          showSuccessMessage(this.$t('BrandInviteAccepted', { brand: invite.brandName }))
          // 加入一个品牌后，其他品牌的邀请在云端已经一并作废
          this.invites = []
        } else {
          await rejectShopInvite(invite.id, deviceId)
          showSuccessMessage(this.$t('BrandInviteRejected'))
          this.invites = this.invites.slice(1)
        }
      } catch (e) {
        showErrorMessage(this.$t('BrandInviteFailed', { message: e.message }))
      } finally {
        this.working = false
        this.confirming = null
      }
    }
  }
}
</script>
